/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { get, head, isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import {
  Icon,
  LoadingIcon,
  PrimaryButton,
  SecondaryButton
} from 'app/common/components';
import {
  useCartInfo,
  useCartPaymentApi,
  useCurrentApplication,
  useFormatMessage,
  useHistory,
  useModifyCartRequest,
  useNationalSiteContext,
  usePaymentAuthState
} from 'app/common/hooks';
import { CartContext, PaymentContext } from 'app/common/contexts';

import messages from './PassthroughPaymentInfoEditable.messages';
import { Environment } from 'app/common/services';
import { REVIEW_PATH } from 'app/checkout/components/CheckoutLayout/CheckoutPaths';
import { goToCheckoutStep } from 'app/checkout/components/CheckoutLayout/components/hooks';
import { useArchivePayment } from '@broadleaf/payment-react';
import { usePaymentClient } from 'app/core/helpers/ClientProvider/ClientProvider.js';
import { CheckoutContext } from 'app/checkout/contexts';
import { clearFleetAddressDeselected } from 'app/checkout/contexts/reducers/checkoutReducer.js';
import {
  usePayInStoreMessageContent,
  default as usePayInStoreMessaging
} from 'app/common/hooks/payInStore/usePayInStoreMessaging';

/**
 * Render component for the editable form fields for the checkout's payment
 * stage.
 *
 * @visibleName Checkout: Payment Info Form
 * @author [Nathan Moore](https://github.com/nathandmoore)
 */
const PassthroughPaymentInfoEditable = ({
  active = false,
  completed,
  isPayInStore
}) => {
  const formatMessage = useFormatMessage();
  const { cart, setCart } = React.useContext(CartContext);
  const goToNextStep = useGoToReviewStep();
  const {
    checkoutOperations: { baseUrl }
  } = useCartInfo();
  const {
    error,
    exception,
    sendCallback: updateCartInfo
  } = useModifyCartRequest(`${baseUrl}/${cart.id}`, null, false);
  const isPayInStoreSet = get(cart, 'attributes.PAY_IN_STORE', false);
  const dealer = useCurrentApplication() || {};
  const { dealerNetwork } = useNationalSiteContext();
  const nameKey = dealerNetwork ? 'displayName' : 'name';

  const authState = usePaymentAuthState();
  const { payments, refetchPayments } = useContext(PaymentContext);
  const paymentClient = usePaymentClient();
  const { archivePayment } = useArchivePayment({ paymentClient, authState });
  const { checkoutDispatch } = useContext(CheckoutContext);

  usePayInStoreMessaging();
  const payInStoreMessagesContent = usePayInStoreMessageContent();

  const paragraphHtml = get(
    payInStoreMessagesContent,
    'fields.payInStoreHelpText.value',
    null
  );
  const payingInStoreText = get(
    payInStoreMessagesContent,
    'fields.payingInStoreText.value',
    formatMessage(messages.payInStore)
  );

  return (
    <Formik
      initialValues={{ attributes: { PAY_IN_STORE: true }, activePayments: [] }}
      onSubmit={async (values, actions) => {
        const { attributes } = values;
        clearFleetAddressDeselected(checkoutDispatch);
        actions.setSubmitting(true);
        const cartVersionHeaderName = Environment.get(
          'cart.version.header',
          'X-Cart-Version'
        );

        if (!isEmpty(payments) && !isEmpty(payments.content)) {
          for (let payment of payments.content) {
            await archivePayment(payment.paymentId, payment.version);
          }
          await refetchPayments();
        }

        const updatedCart = await updateCartInfo({
          method: 'patch',
          data: {
            purchaseOrderNumber: cart.purchaseOrderNumber,
            attributes
          },
          headers: {
            [cartVersionHeaderName]: cart.version
          }
        });
        actions.setSubmitting(false);

        if (!isEmpty(updatedCart)) {
          setCart(updatedCart);

          if (error) {
            return;
          }

          goToNextStep();
        }
      }}
    >
      {({ isSubmitting }) => {
        return (
          <Form>
            {error && !isSubmitting && (
              <div className="flex items-center mb-4 px-2 py-1 text-sm text-red-600 leading-snug border border-solid border-red-200 bg-red-100 rounded md:px-4 md:py-2 lg:text-base lg:leading-normal">
                <Icon className="mr-2 md:mr-4" name="exclamation-circle" />
                <span>
                  {exception.exception || formatMessage(messages.genericError)}
                </span>
              </div>
            )}
            <div className="mr-16">
              <div className="font-bold mb-2">{payingInStoreText}</div>
              <div>{get(dealer, nameKey)}</div>
              {paragraphHtml && (
                <div
                  className="mt-2"
                  dangerouslySetInnerHTML={{ __html: paragraphHtml }}
                />
              )}
            </div>
            <div className="flex justify-end mt-4">
              {completed && isPayInStoreSet && (
                <SecondaryButton
                  className="mr-2"
                  disabled={isSubmitting || !active}
                  onClick={goToNextStep}
                >
                  {formatMessage(messages.cancel)}
                </SecondaryButton>
              )}
              <PrimaryButton
                className="relative"
                type="submit"
                disabled={isSubmitting || !active || !isPayInStore}
              >
                <div
                  className={classNames({ 'text-transparent': isSubmitting })}
                >
                  {formatMessage(messages.submit)}
                </div>
                {isSubmitting && (
                  <div className="absolute inset-0 flex justify-center items-center">
                    <LoadingIcon className="text-blue-500" />
                  </div>
                )}
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

function useGoToReviewStep() {
  const history = useHistory();
  return goToCheckoutStep(history, REVIEW_PATH, {
    paymentComplete: true,
    editing: false
  });
}

PassthroughPaymentInfoEditable.GatewayType = 'PASSTHROUGH';

PassthroughPaymentInfoEditable.propTypes = {
  /**
   * Whether the payment info stage is active. Affects whether the form is
   * disabled.
   */
  active: PropTypes.bool
};

export default PassthroughPaymentInfoEditable;
export { PassthroughPaymentInfoEditable };
